import React, { createContext, SetStateAction, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePost } from '../API';
import * as v0 from '@aidkitorg/types/lib/survey';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import {
  Dashboard,
  DashboardComponentWithId,
  StagedComponent,
  SupportedDashboardComponent,
  DashboardComponentResult
} from '@aidkitorg/types/lib/admin-panel/dashboard';
import { FilterConfig, BooleanExprToFilterGroupConverter, FilterGroupToBooleanExprConverter } from '@aidkitorg/types/lib/admin-panel/filters';
import { BooleanExpr, DashboardWithId } from '@aidkitorg/types/lib/survey';
import { Layout } from 'react-grid-layout';
import { nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';
import { useApplicantGroupExpansion } from '../AdminPanel/shared/hooks/useApplicantGroupFilters';
import InterfaceContext from '../Context';
import { adminPanelComponentsToDistro } from '../AdminPanel/Dashboards/adapters/adminPanelToDistro';
import { isReviewDashboard, hasTemplatedComponent } from '../utils/dashboardUtils';
import type { DashboardReport } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { adaptDashboardComponents, type AdaptedDashboardComponent } from '../AdminPanel/Dashboards/adapters/distroToAdminPanel';
import { assignDefaultStyles } from '../AdminPanel/Dashboards/utils/defaultDashboardLayout';

/** Convert distro-only component kinds (e.g. Report Reference) to their admin panel equivalents */
export function distroComponentToAdminPanel(comp: any): SupportedDashboardComponent {
  if (comp.kind === 'Report Reference') {
    return {
      kind: 'Report',
      title: 'Report',
      reportId: comp.reportId,
    } satisfies DashboardReport;
  }
  return comp as SupportedDashboardComponent;
}

/**
 * Replace the client-side __synthetic_uid__ placeholder with the real `uid` column name.
 * Works on any JSON-serializable value (BooleanExpr, full component, etc.) since the
 * placeholder only ever appears as a quoted string in the serialized payload.
 */
function replaceSyntheticUid<T>(value: T): T {
  return JSON.parse(JSON.stringify(value).replace(/"__synthetic_uid__"/g, '"uid"'));
}

// Cache types
type CacheKey = string;
type CacheEntry = {
  data: DashboardWithComponents;
  timestamp: number;
  ttl: number;
};
type DashboardCache = Map<CacheKey, CacheEntry>;

const CACHE_MAX_ENTRIES = 10;
const CACHE_TTL_MS = 600000; // 10 minute TTL for dashboards

// Per-component result cache — short-circuits loadDashboardComponentData so
// fresh card mounts (edit-mode entry, post-save navigation, view → edit → view)
// reuse the data instead of re-hitting /load. Keyed on what determines the
// response: the server-assigned dashboardRef (which already encodes filter)
// or the full (component, viewFilters, dashboardFilter) tuple for previews.
type ComponentDataCacheEntry = {
  data: DashboardComponentLoadReturnType;
  timestamp: number;
};
type ComponentDataCache = Map<string, ComponentDataCacheEntry>;

const COMPONENT_DATA_CACHE_MAX_ENTRIES = 200;
const COMPONENT_DATA_CACHE_TTL_MS = 600000; // 10 minutes

// Module-level singletons so caches survive DashboardsProvider remounts.
// This helps us avoide re-calling getDashbaords and load when we do not have to.
const dashboardCache: DashboardCache = new Map();
const componentDataCache: ComponentDataCache = new Map();

/** Mirror of the repository's AncestorRef ({ id, name }); name is raw localizable Text. */
export type DashboardAncestor = { id: string; name: v0.Text | string };

export type DashboardWithComponents = Dashboard & {
  lastUpdated: number,
  // force refresh every card on the dash
  forceRefreshKey: number,
  components: Array<DashboardComponentWithId>,
  filter?: BooleanExpr,
  rawDistroDashboard?: DashboardWithId,
  versionId: string,
  adapted: AdaptedDashboardComponent[],
  adaptedMap: Map<string, { adapted: AdaptedDashboardComponent; index: number }>,
}

export type HeroNumberWithFields = v0.HeroNumberWithFields
export type BarChart = v0.BarChart
export type PieChart = v0.PieChart

type DashboardComponentLoadReturnType = {
  success: boolean,
  data?: DashboardComponentResult,
  errorCode?: number,
  errorMessage?: string,
}

/** Optional stable ids for the /load API / DataDog tagging (omit for ephemeral preview requests). */
export type DashboardComponentLoadMetadata = {
  componentId?: string
  dashboardId?: string
  dashboardRef?: string
  dashboardRefs?: Record<string, string>
  summaryKey?: string
  groupKeys?: string[]
  skipCache?: boolean
}

type DashboardsContextType = {
  dashboards: Array<Dashboard>
  reviewDashboards: Array<Dashboard>
  dashboardsById: DashboardsById,
  dashboardErrors: Record<string, string>,
  getDashboards: () => void
  getReviewDashboards: () => void
  isDashboardsLoading: boolean,
  hasInitialLoad: boolean,

  fetchDashboardById: ({ id, forceRefresh, viewFilter, filterIndexes, bumpRefreshKey, forEditing }: { id: string, forceRefresh?: boolean, viewFilter?: BooleanExpr, filterIndexes?: Record<string, number>, bumpRefreshKey?: boolean, forEditing?: boolean }) => Promise<void>,
  getDashboardById: (id: string) => DashboardWithComponents | void,
  /** Fetches just the Collection ancestor chain (root→leaf) for a dashboard, for breadcrumbs.
   * The dashboard view loads through the legacy /dashboard/getDashboards data endpoint,
   * which has no ancestry, so this hits the kind-service get separately. */
  getDashboardAncestors: (id: string) => Promise<DashboardAncestor[]>,

  deleteDashboardById: (id: string) => void,
  cloneDashboard: (dashboardId: string, newTitle: string) => Promise<string | void>,
  isCloning: boolean,

  // Dashboard Builder
  stagedComponents: Array<StagedComponent | DashboardComponentWithId>,
  setStagedComponents: React.Dispatch<SetStateAction<Array<StagedComponent | DashboardComponentWithId>>>,

  createDashboard: (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>, filter?: BooleanExpr) => Promise<string | void>,
  updateDashboard: (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>, versionId: string, filter?: BooleanExpr) => Promise<string | void>,
  saveTemplateStyling: (dashboardId: string, layout: Array<Layout>, versionId: string) => Promise<string | void>,
  loadDashboardComponentData: (component: SupportedDashboardComponent, viewFilters?: FilterConfig, dashboardFilter?: BooleanExpr, metadata?: DashboardComponentLoadMetadata) => Promise<DashboardComponentLoadReturnType | void>

  toggleDashboardLocked: (id: string) => Promise<void>
  logDashboardDownload: (id: string) => Promise<boolean>
}

type DashboardsById = Record<string, DashboardWithComponents>

type DashboardsProviderProps = {
  children: React.ReactNode
}

const DashboardsContext = createContext<DashboardsContextType>({
  dashboards: [],
  reviewDashboards: [],
  dashboardsById: {},
  dashboardErrors: {},
  getDashboards: async () => {},
  getReviewDashboards: async () => {},

  isDashboardsLoading: false,
  hasInitialLoad: false,

  fetchDashboardById: async ({}: { id: string, forceRefresh?: boolean, viewFilter?: BooleanExpr, filterIndexes?: Record<string, number>, bumpRefreshKey?: boolean, forEditing?: boolean }) => {},
  getDashboardById: () => {},
  getDashboardAncestors: async () => [],

  deleteDashboardById: async () => {},
  cloneDashboard: async (_dashboardId, _newTitle) => {},
  isCloning: false,

  stagedComponents: [],
  setStagedComponents: (_prev) => {},

  createDashboard: async (_title, _desc, _components, _layout, _filter?) => {},
  updateDashboard: async (_id, _title, _desc, _components, _layout, _versionId, _filter?) => {},
  saveTemplateStyling: async (_dashboardId, _layout, _versionId) => {},
  loadDashboardComponentData: async (_component: SupportedDashboardComponent, _viewFilters?: FilterConfig, _dashboardFilter?: BooleanExpr, _metadata?: DashboardComponentLoadMetadata) => {},

  toggleDashboardLocked: async (_id) => {},
  logDashboardDownload: async (_id) => { return true },
})

export const useDashboards = () => {
  const context = useContext(DashboardsContext)
  if (!context) {
    throw new Error("useDashboards must be used within a DashboardsProvider")
  }

  return context;
}

/** Check whether a component tree contains any Subsurvey Summary components. */
function containsSubsurveySummary(components: any[]): boolean {
  for (const comp of components) {
    if (comp.kind === 'Subsurvey Summary') return true;
    if (Array.isArray(comp.components) && containsSubsurveySummary(comp.components)) return true;
  }
  return false;
}

/** Stable key for a Dashboard Section, used to pair raw with staged. */
function sectionKey(section: any): string | null {
  if (!section?.title) return null;
  return typeof section.title === 'string' ? section.title : JSON.stringify(section.title);
}

/** Whether a component subtree contains any Subsurvey Summary. */
function subtreeContainsSubsurveySummary(components: any[]): boolean {
  for (const c of components) {
    if (c?.kind === 'Subsurvey Summary') return true;
    if (Array.isArray(c?.components) && subtreeContainsSubsurveySummary(c.components)) return true;
  }
  return false;
}

/**
 * Staged components are the "editable" components in the dashboard builder -
 * Subsurvey Summaries and their expanded children are not included in the
 * staged list. We must re-insert the original Subsurvey Summary components
 * before updating the dashboard so they aren't lost.
 *
 * Strategy:
 *   - The user's `staged` list owns the ordering and edits of all non-SS
 *     components. Deletes, reorders, and additions are honored as-is, with no
 *     positional pairing against `raw` (which would drift on any deletion).
 *   - Dashboard Sections are paired between staged and raw by title so we can
 *     recurse and preserve nested Subsurvey Summaries inside the right section.
 *   - Any Subsurvey Summary from `raw` that wasn't already preserved gets
 *     appended at the end of its level. Visual placement of SS may shift, but
 *     no SS data is ever dropped.
 *   - Safety net: a raw section whose title can't be matched but contains an
 *     SS is appended verbatim so the SS inside it survives.
 */
function mergeSubsurveySummary(staged: any[], raw: any[]): any[] {
  const rawSectionsByKey = new Map<string, any>();
  for (const rawComp of raw) {
    if (rawComp?.kind === 'Dashboard Section') {
      const key = sectionKey(rawComp);
      if (key && !rawSectionsByKey.has(key)) rawSectionsByKey.set(key, rawComp);
    }
  }

  const matchedSectionKeys = new Set<string>();
  const result: any[] = [];

  for (const stagedComp of staged) {
    if (stagedComp?.kind === 'Dashboard Section') {
      const key = sectionKey(stagedComp);
      const matchingRaw = key ? rawSectionsByKey.get(key) : undefined;
      if (matchingRaw) {
        if (key) matchedSectionKeys.add(key);
        result.push({
          ...stagedComp,
          components: mergeSubsurveySummary(
            stagedComp.components || [],
            matchingRaw.components || []
          ),
        });
        continue;
      }
    }
    result.push(stagedComp);
  }

  for (const rawComp of raw) {
    if (rawComp?.kind === 'Subsurvey Summary') {
      result.push(rawComp);
    } else if (rawComp?.kind === 'Dashboard Section') {
      const key = sectionKey(rawComp);
      const wasMatched = key ? matchedSectionKeys.has(key) : false;
      if (!wasMatched && Array.isArray(rawComp.components) && subtreeContainsSubsurveySummary(rawComp.components)) {
        result.push(rawComp);
      }
    }
  }

  return result;
}

/**
 * Recursively strip the transient `_restricted` flag (stamped by the backend on locked
 * placeholders in `forEditing` loads) from a distro component tree before it is saved.
 * Returns new objects so the in-memory render state keeps its flag. The server-side
 * `restoreHiddenComponents` backstop covers top-level restricted components; this also
 * cleans restricted components nested inside a visible Dashboard Section.
 */
function stripRestrictedFlag(components: any[]): any[] {
  return components.map((comp) => {
    if (!comp || typeof comp !== 'object') return comp;
    const { _restricted, ...rest } = comp;
    if (Array.isArray(rest.components)) {
      rest.components = stripRestrictedFlag(rest.components);
    }
    return rest;
  });
}

export const DashboardsProvider: React.FC<DashboardsProviderProps> = ({ children }) => {
  // TODO: update this array type
  const [dashboards, setDashboards] = useState<Array<Dashboard>>([])
  const [reviewDashboards, setReviewDashboards] = useState<Array<Dashboard>>([])
  const [hasInitialLoad, setHasInitialLoad] = useState(false)
  const [isDashboardsLoading, setIsDashboardsLoading] = useState(false)

  const [stagedComponents, setStagedComponents] = useState<Array<StagedComponent>>([])

  const [dashboardsById, setDashboardsById] = useState<DashboardsById>({})
  const [dashboardErrors, setDashboardErrors] = useState<Record<string, string>>({})

  const dashboardCacheRef = useRef<DashboardCache>(dashboardCache)
  const componentDataCacheRef = useRef<ComponentDataCache>(componentDataCache)

  // Per-dashboard_id request sequencing. Multiple fetches for the *same* dashboard_id can be
  // in flight at once. We need to make sure the most recent one is the one that wins.
  const dashboardRequestSeqRef = useRef<Record<string, number>>({})

  const isStaleDashboardRequest = useCallback((id: string, seq: number) => {
    return dashboardRequestSeqRef.current[id] !== seq;
  }, []);

  // Hash function for cache keys (similar to Reports)
  const hashString = useCallback((str: string): string => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }, []);

  // Cache utility functions
  const generateCacheKey = useCallback((dashboardId: string) => {
    return hashString(`dashboard-${dashboardId}`);
  }, [hashString]);

  const getCachedDashboard = useCallback((cacheKey: string): DashboardWithComponents | null => {
    const cache = dashboardCacheRef.current;
    const entry = cache.get(cacheKey);
    
    if (!entry) return null;
    
    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      console.log("delete cached result")
      cache.delete(cacheKey);
      return null;
    }
    
    return entry.data;
  }, []);

  const setCachedDashboard = useCallback((cacheKey: string, data: DashboardWithComponents, ttlMs: number = CACHE_TTL_MS) => {
    const cache = dashboardCacheRef.current;
        
    // Lazy cleanup: remove expired entries when cache gets too big
    if (cache.size >= CACHE_MAX_ENTRIES) {
      const now = Date.now();
      const entries = Array.from(cache.entries());
      
      // Remove expired entries
      for (const [key, entry] of entries) {
        if (now > entry.timestamp + entry.ttl) {
          cache.delete(key);
        }
      }
      
      // If still too big after cleanup, remove oldest entry
      if (cache.size >= CACHE_MAX_ENTRIES) {
        const remainingEntries = Array.from(cache.entries());
        remainingEntries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        cache.delete(remainingEntries[0][0]);
      }
    }
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
  }, []);

  const interfaceContext = useContext(InterfaceContext);
  const queryClient = useQueryClient();
  const DISTRO_DASHBOARDS_KEY = ['distro-dashboards'];

  // Endpoints used to display dashboards
  const getDashboardsPost = usePost("/dashboard/getDashboards", { suppressErrorDisplay: true })
  const loadDashboardPost = usePost("/dashboard/load", { suppressErrorDisplay: true })
  
  // CRUD endpoints for admin panel dashboards
  const listDashboardsPost = usePost("/distro/dashboard/list")
  const getDashboardPost = usePost("/distro/dashboard/get")
  // Separate instance for best-effort breadcrumb ancestry: failures fall back to
  // an empty chain silently, so suppress the global API error toast.
  const getDashboardAncestryPost = usePost("/distro/dashboard/get", { suppressErrorDisplay: true })
  const createDashboardPost = usePost("/distro/dashboard/create")
  const updateDashboardPost = usePost("/distro/dashboard/update")
  const deleteDashboardPost = usePost("/distro/dashboard/delete")
  const logAuditEntryPost = usePost("/api/admin_panel/log_audit_entry")
  const { expandFilterConfig, expandBooleanExpr } = useApplicantGroupExpansion();

  const invalidateDistroCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: DISTRO_DASHBOARDS_KEY });
  }, [queryClient]);

  const transformDistroDashboard = useCallback((dashboard: DashboardWithId): Dashboard => ({
    id: dashboard.id,
    title: typeof dashboard.title === 'string'
      ? dashboard.title
      : dashboard.title?.[interfaceContext.lang] || dashboard.title?.en || '',
    description: typeof dashboard.description === 'string'
      ? dashboard.description
      : dashboard.description?.[interfaceContext.lang] || dashboard.description?.en || '',
    created_by: dashboard.createdByName || dashboard.createdBy || 'System',
    created_by_uid: dashboard.createdBy || '',
    created_at: dashboard.createdAt || 0,
    updated_at: dashboard.updatedAt || 0,
    locked: dashboard.locked ?? false,
    view_only: false,
    source: 'survey',
    href: dashboard.path,
    _fromTemplatedBlock: (dashboard as any)._fromTemplatedBlock,
    _hasTemplatedComponents: hasTemplatedComponent(dashboard.components || []),
  }), [interfaceContext.lang]);

  const fetchRawDashboards = async (): Promise<DashboardWithId[]> => {
    let rawDashboards = queryClient.getQueryData<DashboardWithId[]>(DISTRO_DASHBOARDS_KEY);
    if (!rawDashboards) {
      // usePost returns { error } without throwing (even when the toast is shown).
      // Only cache real arrays — coercing errors to [] poisons the shared key
      // (empty arrays are truthy, so later readers never refetch).
      const result = await listDashboardsPost({});
      if (!Array.isArray(result)) {
        throw new Error('Failed to list dashboards');
      }
      rawDashboards = result;
      queryClient.setQueryData(DISTRO_DASHBOARDS_KEY, rawDashboards);
    }
    return rawDashboards;
  }

  const loadAllDashboards = async () => {
    setIsDashboardsLoading(true)
    try {
      const rawDashboards = await fetchRawDashboards();

      // Transform distro dashboards and split into regular vs review
      const regular = rawDashboards.filter(d => !isReviewDashboard(d));
      const review = rawDashboards.filter(isReviewDashboard);
      const distroDashboards = regular.map(transformDistroDashboard);
      const distroReviewDashboards = review.map(transformDistroDashboard);

      setDashboards(distroDashboards)
      setReviewDashboards(distroReviewDashboards)
      setHasInitialLoad(true)
    } catch (error) {
      console.error('Error fetching dashboards:', error);
    } finally {
      setIsDashboardsLoading(false)
    }
  }

  const getDashboards = async () => {
    await loadAllDashboards()
  }

  const getReviewDashboards = async () => {
    await loadAllDashboards()
  }

  // wrapper around dashboards/getDashboard endpoint - fetches config for dashboard components, but does not 
  // fetch the actual data to load the components.
  const fetchDashboardById = async ({ id, forceRefresh = false, viewFilter, filterIndexes, bumpRefreshKey = false, forEditing = false }: { id: string, forceRefresh?: boolean, viewFilter?: BooleanExpr, filterIndexes?: Record<string, number>, bumpRefreshKey?: boolean, forEditing?: boolean }) => {
    // Claim the next sequence token for this dashboard_id up front, so any fetch after this is definitely higher
    const seq = (dashboardRequestSeqRef.current[id] = (dashboardRequestSeqRef.current[id] ?? 0) + 1);

    // Skip cache when filters are applied — the cached version has stale dashboardRefs.
    // Also skip when loading for the builder (forEditing): that response includes locked
    // `_restricted` placeholders that must never bleed into the read-only view page (and
    // vice versa), since both share this cache key.
    if (!forceRefresh && !viewFilter && !filterIndexes && !forEditing) {
      const cacheKey = generateCacheKey(id);
      const cachedDashboard = getCachedDashboard(cacheKey);

      if (cachedDashboard) {
        if (isStaleDashboardRequest(id, seq)) return;
        setDashboardsById((prev) => ({
          ...prev,
          [id]: cachedDashboard
        }));
        return;
      }
    }

    try {
      await fetchDistroDashboardById({ id, viewFilter, filterIndexes, bumpRefreshKey, forEditing, seq });
      if (isStaleDashboardRequest(id, seq)) return;
      // Clear any previous error for this dashboard
      setDashboardErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (e) {
      if (isStaleDashboardRequest(id, seq)) return;
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage === 'Forbidden') {
        setDashboardErrors((prev) => ({ ...prev, [id]: 'forbidden' }));
        return;
      }

      setDashboardErrors((prev) => ({ ...prev, [id]: 'not_found' }));
    }
  }

  const getDashboardAncestors = useCallback(async (id: string): Promise<DashboardAncestor[]> => {
    try {
      const response = await getDashboardAncestryPost({ id, includeAncestry: true });
      return ((response.result as { ancestors?: DashboardAncestor[] })?.ancestors) ?? [];
    } catch {
      return [];
    }
  }, [getDashboardAncestryPost]);

  const invalidateAndRefreshDashboard = async (dashboardId: string) => {
    invalidateDistroCache();
    const cacheKey = generateCacheKey(dashboardId);
    dashboardCacheRef.current.delete(cacheKey);
    await fetchDashboardById({ id: dashboardId, forceRefresh: true });
  };

  const fetchDistroDashboardById = async ({ id, viewFilter, filterIndexes, bumpRefreshKey = false, forEditing = false, seq }: { id: string, viewFilter?: BooleanExpr, filterIndexes?: Record<string, number>, bumpRefreshKey?: boolean, forEditing?: boolean, seq?: number }) => {
    const expandedViewFilter = viewFilter ? await expandBooleanExpr(viewFilter) as BooleanExpr | undefined : undefined;
    // Replace __synthetic_uid__ with uid so the backend SQL uses the correct column
    const translatedViewFilter = expandedViewFilter ? replaceSyntheticUid(expandedViewFilter) : undefined;

    // The saved Dashboard.filter stores applicant group references as synthetic
    // `applicant_group` conditions. Resolve them to the group's real BooleanExpr on the client
    // (via /distro/applicant_group/get, the same path the All Applicants table uses) and send
    // the expanded filter so the backend compiles SQL against actual membership conditions
    // rather than the non-existent `applicant_group` column. The stored filter is left
    // synthetic so the builder's filter panel can still round-trip the group reference.
    //
    // Expansion drops references to deleted groups rather than broadening the filter: e.g. "in
    // group A OR in group B" with B deleted resolves to "in group A", and a sole deleted group
    // resolves to undefined — in which case we send no override so the backend fails closed on
    // the stored (still-synthetic) filter rather than exposing every applicant.
    let dashboardFilter: BooleanExpr | undefined;
    try {
      const rawDashboards = await fetchRawDashboards();
      const storedFilter = rawDashboards.find(d => d.id === id)?.filter;
      if (storedFilter) {
        const expanded = await expandBooleanExpr(storedFilter);
        dashboardFilter = expanded ? replaceSyntheticUid(expanded) : undefined;
      }
    } catch (e) {
      console.warn('[DashboardsContext] Failed to expand applicant group references in dashboard filter:', e);
    }

    const loadDashboard = async (): Promise<{ dashboard: DashboardWithId, versionId: string }> => {
      const response = await getDashboardsPost({
        dashboard: id,
        dashboardId: id,
        ...(translatedViewFilter ? { viewFilter: translatedViewFilter } : {}),
        ...(dashboardFilter ? { dashboardFilter } : {}),
        ...(filterIndexes && Object.keys(filterIndexes).length > 0 ? { filterIndexes } : {}),
        ...(forEditing ? { forEditing } : {}),
      }) as any;

      if (response?.error) {
        throw new Error(response.error);
      }

      const dashboard = response as DashboardWithId;
      if (!dashboard || !dashboard.id) {
        throw new Error(`Dashboard ${id} not found`);
      }

      return { dashboard, versionId: (response as any).versionId ?? '' };
    };

    // `versionId` is the base for the next write from this editor, so it has to keep
    // describing the components held here — see the re-read after the style save below.
    let { dashboard: distroDashboard, versionId } = await loadDashboard();
    let distroComponents = distroDashboard.components || [];

    // Check if any components lack _style — if so, generate defaults and persist.
    // Skip persistence for templated dashboards to avoid destroying the TemplatedBlock
    // in the distro survey (the backend expands templates before saving).
    const isFromTemplatedBlock = (distroDashboard as any)._fromTemplatedBlock === true;
    const hasTemplatedComponents = hasTemplatedComponent(distroComponents);
    const hasExpandedSubsurveySummary = distroComponents.some((comp: any) => comp._expandedFrom === 'Subsurvey Summary');
    const needsStyleGeneration = distroComponents.some((comp: any) => !comp._style);
    if (needsStyleGeneration) {
      // Each adapted entry's `original` is the distro component itself, so this writes
      // through to `distroComponents` — which is what gets persisted and re-adapted below.
      assignDefaultStyles(adaptDashboardComponents(distroComponents), distroDashboard.id);

      // Only persist generated styles from builder loads — a view-page load must never
      // write back the definition (its components carry runtime mutations like
      // substituted Custom Query SQL, and older backends don't send the rawSql marker
      // the server-side restore needs).
      if (forEditing && !isFromTemplatedBlock && !hasTemplatedComponents && !hasExpandedSubsurveySummary) {
        try {
          const config = {
            kind: 'Dashboard' as const,
            title: distroDashboard.title,
            ...(distroDashboard.description ? { description: distroDashboard.description } : {}),
            path: distroDashboard.path,
            // Strip transient `_restricted` placeholder flags; never persist them.
            components: stripRestrictedFlag(distroComponents),
            ...(distroDashboard.filter ? { filter: distroDashboard.filter } : {}),
            version: 1 as const,
          };
          await updateDashboardPost({ id: distroDashboard.id, config, baseVersionId: versionId });

          // Re-read rather than carrying on with the version loaded before this save. That
          // version is now stale, and the one the save publishes is no better: a publish can
          // fold in edits made concurrently by someone else, so it may describe components
          // that were never rendered here — and a base version that names content the editor
          // is not showing lets the next full save drop it without registering a conflict.
          ({ dashboard: distroDashboard, versionId } = await loadDashboard());
          distroComponents = distroDashboard.components || [];
          // Styles the save did not keep (a component whose `viewableBy` excludes this
          // user is restored from the stored copy) are filled in for display only.
          assignDefaultStyles(adaptDashboardComponents(distroComponents), distroDashboard.id);
        } catch (e) {
          console.warn('[DashboardsContext] Failed to persist default styles:', e);
        }
      }
    }

    const title = typeof distroDashboard.title === 'string'
      ? distroDashboard.title
      : distroDashboard.title?.[interfaceContext.lang] || distroDashboard.title?.en || '';

    const description = typeof distroDashboard.description === 'string'
      ? distroDashboard.description
      : distroDashboard.description?.[interfaceContext.lang] || distroDashboard.description?.en || '';

    const finalAdapted = adaptDashboardComponents(distroComponents);
    const adaptedMap = new Map<string, { adapted: AdaptedDashboardComponent; index: number }>();
    const components: DashboardComponentWithId[] = [];

    function registerAdapted(adapted: AdaptedDashboardComponent, idx: number, idPrefix: string) {
      const compId = `${idPrefix}-${idx}`;
      adaptedMap.set(compId, { adapted, index: idx });
      if (adapted.children) {
        adapted.children.forEach((child, childIdx) => {
          registerAdapted(child, childIdx, compId);
        });
      }
    }

    finalAdapted.forEach((adapted, idx) => {
      const compId = `${distroDashboard.id}-${idx}`;
      const origComp = adapted.original as any;
      const style = origComp?._style
        ? { i: compId, x: origComp._style.x, y: origComp._style.y, w: origComp._style.w, h: origComp._style.h }
        : undefined;

      registerAdapted(adapted, idx, distroDashboard.id);

      // Expanded Subsurvey Summary children are display-only — keep them in
      // adaptedMap for rendering but exclude from the saveable components list.
      // This is very important so they don't get written to Distro and overwrite the 
      // original Subsurvey Summary components!!!
      if (origComp?._expandedFrom === 'Subsurvey Summary') return;

      components.push({
        id: compId,
        config: adapted.adapted || distroComponentToAdminPanel(origComp),
        style,
        staticWidgetId: origComp?.staticWidgetId,
        ...origComp?.dashboardRef ? { dashboardRef: origComp.dashboardRef } : {},
        ...origComp?.dashboardRefs ? { dashboardRefs: origComp.dashboardRefs } : {},
        ...origComp?.summaryKey ? { summaryKey: origComp.summaryKey } : {},
        ...origComp?.groupKeys ? { groupKeys: origComp.groupKeys } : {},
      } as DashboardComponentWithId);
    });

    const previousKey = dashboardsById[distroDashboard.id]?.forceRefreshKey ?? 0;
    const dashboardWithComponents: DashboardWithComponents = {
      id: distroDashboard.id,
      title,
      description,
      created_by: distroDashboard.createdByName || distroDashboard.createdBy || 'System',
      created_by_uid: distroDashboard.createdBy || '',
      created_at: distroDashboard.createdAt || 0,
      updated_at: distroDashboard.updatedAt || 0,
      locked: distroDashboard.locked ?? false,
      components,
      adapted: finalAdapted,
      adaptedMap,
      lastUpdated: Date.now(),
      forceRefreshKey: bumpRefreshKey ? previousKey + 1 : previousKey,
      source: 'survey',
      href: distroDashboard.path,
      rawDistroDashboard: distroDashboard,
      versionId,
      ...(distroDashboard.filter ? { filter: distroDashboard.filter } : {}),
      _fromTemplatedBlock: (distroDashboard as any)._fromTemplatedBlock,
    }

    if (!viewFilter && !filterIndexes && !forEditing) {
      const cacheKey = generateCacheKey(id);
      // Cache regardless of staleness below — this response is still valid data for future
      // unfiltered loads of this dashboard, even if a newer in-flight request supersedes it
      // for the *currently displayed* state.
      setCachedDashboard(cacheKey, dashboardWithComponents);
    }

    // Drop this response if a newer fetch for this dashboard_id has been issued since
    if (seq !== undefined && isStaleDashboardRequest(id, seq)) return;

    setDashboardsById((prev) => ({
      ...prev,
      [distroDashboard.id]: dashboardWithComponents
    }))
  }

  const getDashboardById = (id: string) => {
    return dashboardsById[id]
  }

  const deleteDashboardById = async (id: string) => {
    try {
      await deleteDashboardPost({ id });

      setDashboards(dashboards.filter((dashboard) => dashboard.id !== id))
      setDashboardsById(prev => {
        const newState = { ...prev }
        delete newState[id]
        return newState
      })

      // Invalidate caches
      const cacheKey = generateCacheKey(id);
      dashboardCacheRef.current.delete(cacheKey);
      invalidateDistroCache();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
    }
  }

  // Clone dashboard mutation (get + create)
  const cloneDashboardMutation = useMutation({
    mutationFn: async ({ dashboardId, newTitle }: { dashboardId: string; newTitle: string }) => {
      const existing = (await getDashboardPost({ id: dashboardId })).result;

      const clonedConfig = {
        kind: 'Dashboard' as const,
        title: { en: newTitle },
        path: `${existing.path}_copy_${Date.now()}`,
        components: existing.components,
        version: 1 as const,
      };

      const { result: created } = await createDashboardPost({ config: clonedConfig });
      if (created && created.id) {
        return created.id as string;
      }
      throw new Error('Failed to clone dashboard');
    },
    onSuccess: async () => {
      try {
        invalidateDistroCache();
        await getDashboards();
      } catch (e) {
        console.warn('[DashboardsContext] Failed to refresh dashboards after clone:', e);
      }
    },
  })

  const cloneDashboard = useCallback(async (dashboardId: string, newTitle: string): Promise<string | void> => {
    try {
      return await cloneDashboardMutation.mutateAsync({ dashboardId, newTitle })
    } catch (error) {
      // Error already logged in mutationFn
      return undefined
    }
  }, [cloneDashboardMutation])

  const isCloning = cloneDashboardMutation.isPending

  // wrapper around the dashboard/load API - fetches the data for a specific component
  const loadDashboardComponentData = async (
    component: SupportedDashboardComponent,
    viewFilters?: FilterConfig,
    dashboardFilter?: BooleanExpr,
    metadata?: DashboardComponentLoadMetadata
  ) => {
    let ref = metadata?.dashboardRef;
    let refs = metadata?.dashboardRefs;
    let summaryKey = metadata?.summaryKey;
    let groupKeys = metadata?.groupKeys;
    const skipCache = metadata?.skipCache;

    const componentKind = (component as any)?.kind;

    if (!ref && !refs && metadata?.dashboardId && metadata?.componentId) {
      const dashboard = dashboardsById[metadata.dashboardId];
      // First try the saveable components list (original definition).
      const comp = dashboard?.components.find(c => c.id === metadata.componentId) as any;
      // Fall back to adaptedMap, which includes expanded Subsurvey Summary children
      // that are excluded from the saveable `components` array. The server attaches
      // dashboardRef/refs/summaryKey/groupKeys to each expanded child's `original`.
      const adaptedOrig = dashboard?.adaptedMap.get(metadata.componentId)?.adapted.original as any;

      const source = comp ?? adaptedOrig;
      if (source?.dashboardRefs) {
        refs = source.dashboardRefs;
      }
      if (source?.dashboardRef) {
        ref = source.dashboardRef;
      }

      if (comp?.dashboardRefs) refs = comp.dashboardRefs;
      if (comp?.dashboardRef) ref = comp.dashboardRef;
      // summaryKey/groupKeys must also fall back to adaptedOrig — for
      // expanded Subsurvey Summary children, `comp` is undefined (those
      // children are filtered out of dashboard.components), and the server
      // attaches these keys to adapted.original. Without this fallback,
      // summaryKey resolves to '' and the Pie Chart processing injects a
      // bogus `"": null` entry into every datum.
      summaryKey = summaryKey ?? comp?.summaryKey ?? adaptedOrig?.summaryKey;
      groupKeys = groupKeys ?? comp?.groupKeys ?? adaptedOrig?.groupKeys;
    }

    // Cache key: dashboardRef/refs already encode the component + dashboard
    // filter (the backend re-issues new ref IDs whenever those change), so
    // we don't include filters in the key for those paths. The preview path
    // has no ref, so we key on the full input tuple.
    //
    // We also include a content hash of the passed `component` prop. In the
    // builder a user can edit a component's config locally; the metadata
    // lookup would still resolve to the *saved* component's dashboardRef,
    // so without `componentHash` the cache would happily return data for
    // the pre-edit config.
    //
    // forceRefreshKey is included so the manual Refresh button busts every
    // entry for that dashboard naturally.
    const dashboardForceRefreshKey = metadata?.dashboardId
      ? dashboardsById[metadata.dashboardId]?.forceRefreshKey ?? 0
      : 0;
    const componentHash = JSON.stringify(component);
    let cacheKey: string;
    if (refs && Object.keys(refs).length > 0) {
      cacheKey = JSON.stringify({ refs, ref, c: componentHash, k: dashboardForceRefreshKey });
    } else if (ref) {
      cacheKey = JSON.stringify({ ref, c: componentHash, k: dashboardForceRefreshKey });
    } else {
      cacheKey = JSON.stringify({ component: componentHash, viewFilters, dashboardFilter, k: dashboardForceRefreshKey });
    }

    const cached = componentDataCacheRef.current.get(cacheKey);
    if (!skipCache && cached && Date.now() - cached.timestamp < COMPONENT_DATA_CACHE_TTL_MS) {
      return cached.data;
    }

    // Helper: cache successful results and surface them. Errors are not
    // cached so transient failures get retried on the next mount.
    const cacheAndReturn = (result: DashboardComponentLoadReturnType): DashboardComponentLoadReturnType => {
      if (result.success) {
        const cache = componentDataCacheRef.current;
        if (cache.size >= COMPONENT_DATA_CACHE_MAX_ENTRIES) {
          // Evict the oldest entry to keep memory bounded.
          const oldest = Array.from(cache.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp)[0];
          if (oldest) cache.delete(oldest[0]);
        }
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
      }
      return result;
    };

    // Components with multiple dashboardRefs (Map layers, Queue stats)
    if (refs && Object.keys(refs).length > 0) {
      const isMap = componentKind === 'Map';
      const resultsMap: Record<string, any> = {};
      for (const [queryName, queryRef] of Object.entries(refs)) {
        const loadParams: any = { dashboardRef: queryRef, ...(skipCache ? { skipCache: true } : {}) };
        if (isMap) loadParams.format = 'geojson';
        resultsMap[queryName] = await loadDashboardPost(loadParams) as any;
      }

      // Also load the primary dashboardRef if present (e.g. Queue in_queue rows + count).
      // For queues we keep the full result (rows included) under the `in_queue` key so the
      // applicants table can render rows per tab; rowCount still drives the count badge.
      let primaryResult: any = undefined;
      if (ref) {
        primaryResult = await loadDashboardPost({ dashboardRef: ref, ...(skipCache ? { skipCache: true } : {}) }) as any;
        // A failed primary ref (the in_queue rows + count) is a hard error: returning
        // success here would leave the in_queue tab empty and the count silently zeroed.
        if (primaryResult?.error) {
          return { success: false, errorCode: 500 } as DashboardComponentLoadReturnType;
        }
        if (primaryResult) {
          resultsMap['in_queue'] = primaryResult;
        }
      }

      const allResults = [...Object.values(resultsMap)];
      const hasError = allResults.some((r: any) => r?.error);
      if (hasError) {
        return { success: false, errorCode: 500 } as DashboardComponentLoadReturnType;
      }

      const latestUpdated = allResults.reduce((max: number, r: any) => Math.max(max, r?.lastUpdated || 0), 0);
      return cacheAndReturn({
        success: true,
        data: {
          summaryKey: '',
          groupKeys: [],
          data: resultsMap,
          timing: undefined,
          rowCount: primaryResult && !primaryResult.error ? primaryResult.rowCount : undefined,
          lastUpdated: latestUpdated || undefined,
        }
      } as DashboardComponentLoadReturnType);
    }

    // Single dashboardRef: standard components
    if (ref) {
      const result = await loadDashboardPost({ dashboardRef: ref, ...(skipCache ? { skipCache: true } : {}) }) as any;

      if (result?.error) {
        return { success: false, errorCode: 500, errorMessage: result.error };
      }

      // Single-ref queues still expose rows under `in_queue` so the applicants
      // table (which reads data.in_queue.rows) matches the multi-ref shape.
      const data = componentKind === 'Queue'
        ? { in_queue: result }
        : (result?.rows ?? []);

      return cacheAndReturn({
        success: true,
        data: {
          summaryKey: summaryKey || '',
          groupKeys,
          data,
          timing: result?.executionTimeMs,
          rowCount: result?.rowCount,
          totalCount: result?.totalCount,
          lastUpdated: result?.lastUpdated,
        }
      } as DashboardComponentLoadReturnType);
    }

    // No dashboardRef — compile and execute directly via /load (e.g. builder previews)
    let componentToLoad: SupportedDashboardComponent = component;

    // Expand applicant-group references inside the component's own filter
    if ('filter' in component && component.filter) {
      const expandedFilter = await expandBooleanExpr(component.filter);
      if (expandedFilter) {
        componentToLoad = { ...componentToLoad, filter: expandedFilter } as SupportedDashboardComponent;
      }
    }

    // Merge dashboardFilter into the component filter so /load applies it
    if (dashboardFilter) {
      const expandedDashboardFilter = await expandBooleanExpr(dashboardFilter);
      if (expandedDashboardFilter) {
        const existing = 'filter' in componentToLoad ? (componentToLoad as any).filter : undefined;
        componentToLoad = {
          ...componentToLoad,
          filter: existing
            ? { kind: 'And', clauses: [existing, expandedDashboardFilter] }
            : expandedDashboardFilter,
        } as SupportedDashboardComponent;
      }
    }

    // Merge viewFilters (FilterConfig → BooleanExpr) into the component filter
    if (viewFilters) {
      const expandedViewFilters = await expandFilterConfig(viewFilters);
      if (expandedViewFilters) {
        const viewFilterExpr = FilterGroupToBooleanExprConverter.convertFromConfig(expandedViewFilters);
        if (viewFilterExpr) {
          const existing = 'filter' in componentToLoad ? (componentToLoad as any).filter : undefined;
          componentToLoad = {
            ...componentToLoad,
            filter: existing
              ? { kind: 'And', clauses: [existing, viewFilterExpr] }
              : viewFilterExpr,
          } as SupportedDashboardComponent;
        }
      }
    }

    // Replace the client-side __synthetic_uid__ placeholder with `uid` everywhere in the
    // assembled component (filter clauses, Summary Table groups, etc.) so the SQL the
    // backend compiles via /load → buildMetadataFromComponent → compileComponentSql
    // references the real column. The legacy /translate path did this server-side.
    const componentToSend = replaceSyntheticUid(componentToLoad);

    const loadResult = await loadDashboardPost({
      dashboardRef: 'preview',
      dashboardComponent: componentToSend as any,
    }) as any;

    if (loadResult?.error) {
      return { success: false, errorCode: 500, errorMessage: loadResult.error } as DashboardComponentLoadReturnType;
    }

    // Derive summaryKey / groupKeys / data shape from the component definition
    const comp = component as any;
    let derivedSummaryKey = '';
    let derivedGroupKeys: string[] = [];
    let derivedData: any = loadResult?.rows ?? [];

    switch (comp.kind) {
      case 'Hero Number':
        derivedSummaryKey = nameForColumn(comp.summary) ?? '';
        break;
      case 'Bar Chart':
        derivedSummaryKey = nameForColumn(comp.summary) ?? '';
        derivedGroupKeys = comp.groups?.map((g: any) => nameForColumn(g) ?? '') ?? [];
        break;
      case 'Pie Chart':
        derivedSummaryKey = nameForColumn(comp.summary) ?? '';
        derivedGroupKeys = comp.groups?.map((g: any) => nameForColumn(g) ?? '') ?? [];
        break;
      case 'Line Chart':
        derivedSummaryKey = nameForColumn(comp.summary) ?? '';
        derivedGroupKeys = comp.groups?.map((g: any) => nameForColumn(g) ?? '') ?? [];
        break;
      case 'Summary Table':
        derivedSummaryKey = comp.summary?.map((s: any, i: number) => nameForColumn(s) ?? `summary_${i}`).join('_') ?? '';
        derivedGroupKeys = comp.groups?.map((g: any) => nameForColumn(g) ?? '') ?? [];
        break;
      case 'Map':
        derivedData = loadResult?.rows ?? {};
        break;
    }

    return cacheAndReturn({
      success: true,
      data: {
        summaryKey: derivedSummaryKey,
        groupKeys: derivedGroupKeys,
        data: derivedData,
        timing: loadResult?.executionTimeMs,
        rowCount: loadResult?.rowCount,
        totalCount: loadResult?.totalCount,
        lastUpdated: loadResult?.lastUpdated,
      }
    } as DashboardComponentLoadReturnType);
  }

  /** Convert staged components to distro format and attach grid layout styles. */
  const buildStyledDistroComponents = (components: Array<StagedComponent>, layout: Array<Layout>) => {
    const distroComponents = adminPanelComponentsToDistro(
      components.map(sc => {
        if (sc.staticWidgetId) {
          return { kind: 'Static Widget Reference' as const, staticWidgetId: sc.staticWidgetId } as any;
        }
        return sc.config;
      })
    );

    // Attach grid layout styles to distro components as _style.
    // Top-level items are paired 1:1 with the flat staged/components array.
    // Section children are matched by layout item ID (parentId-childIdx),
    // recursing into arbitrarily nested sections.
    const layoutById = new Map(layout.map(l => [l.i, l]));

    const applyStylesRecursive = (comp: any, compId: string) => {
      const layoutItem = layoutById.get(compId);
      if (layoutItem) {
        comp._style = { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h };
      }
      if (comp.kind === 'Dashboard Section' && Array.isArray(comp.components)) {
        comp.components.forEach((child: any, childIdx: number) => {
          applyStylesRecursive(child, `${compId}-${childIdx}`);
        });
      }
    };

    for (let idx = 0; idx < distroComponents.length && idx < components.length; idx++) {
      applyStylesRecursive(distroComponents[idx], components[idx].id);
    }

    // Never persist the transient `_restricted` placeholder flag.
    return stripRestrictedFlag(distroComponents);
  };

  const createDashboard = async (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>, filter?: BooleanExpr) => {
    try {
      const distroComponents = buildStyledDistroComponents(components, layout);

      const path = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `dashboard_${Date.now()}`;

      const translatedFilter = filter ? replaceSyntheticUid(filter) : undefined;
      const config = {
        kind: 'Dashboard' as const,
        title: { en: title },
        ...(description ? { description: { en: description } } : {}),
        path,
        components: distroComponents,
        ...(translatedFilter ? { filter: translatedFilter } : {}),
        locked: true,
        version: 1 as const,
      };

      const createPostResponse = await createDashboardPost({ config });
      const { result: created } = createPostResponse;
      if (created && created.id) {
        // Eagerly update local state so the list and view page reflect the new dashboard immediately
        setDashboards(prev => [...prev, transformDistroDashboard(created)]);

        await invalidateAndRefreshDashboard(created.id);

        return created.id as string;
      }
    } catch (error) {
      console.error('Error creating dashboard:', error);
    }
  }

  const updateDashboard = async (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>, versionId: string, filter?: BooleanExpr) => {
    try {
      // Refuse a full overwrite when we don't have the dashboard's base versionId.
      // An empty versionId means the builder is trying to save before the dashboard
      // finished loading, so `title`/`components` still hold their un-populated defaults
      // ('My Dashboard' / []) and this would wipe the stored dashboard. It also tells the
      // backend there's no base version to check against, turning the save into a blind
      // overwrite that clobbers concurrent edits. Bail out; the caller shows an error toast.
      if (!versionId) {
        console.warn('[DashboardsContext] Refusing to update dashboard without a base versionId (dashboard not fully loaded)');
        return;
      }

      // Prevent saving dashboards whose raw components contain templated content —
      // the backend save path expands TemplatedBlocks and would permanently destroy them.
      const rawDashboard = dashboardsById[id]?.rawDistroDashboard;
      const hasTemplatedComponents = hasTemplatedComponent(rawDashboard?.components || []);
      if (hasTemplatedComponents) {
        console.warn('[DashboardsContext] Cannot save dashboard with templated components — would destroy TemplatedBlock structure');
        return id;
      }

      const distroComponents = buildStyledDistroComponents(components, layout);

      // The server expands Subsurvey Summary into individual charts/tables,
      // so stagedComponents never contains the original. Fetch the raw
      // (unexpanded) definition and merge the Subsurvey Summary components
      // back into the save payload so they aren't lost.
      let finalComponents = distroComponents;
      try {
        const rawDef = await getDashboardPost({ id });
        const rawComponents = rawDef?.result?.components as any[] | undefined;
        if (rawComponents && containsSubsurveySummary(rawComponents)) {
          finalComponents = mergeSubsurveySummary(distroComponents, rawComponents);
        }
      } catch (e) {
        console.warn('[DashboardsContext] Could not fetch raw definition to preserve Subsurvey Summary:', e);
      }

      const existingDashboard = dashboardsById[id];
      const existingPath = existingDashboard?.rawDistroDashboard?.path ?? '';

      const translatedFilter = filter ? replaceSyntheticUid(filter) : undefined;
      const config = {
        kind: 'Dashboard' as const,
        title: { en: title },
        ...(description ? { description: { en: description } } : {}),
        path: existingPath,
        components: finalComponents,
        ...(translatedFilter ? { filter: translatedFilter } : {}),
        locked: existingDashboard?.locked ?? false,
        version: 1 as const,
      };

      const updatePostResponse = await updateDashboardPost({ id, config, baseVersionId: versionId });

      if ('error' in updatePostResponse) {
        throw new Error(String(updatePostResponse.error));
      }

      const { result: updated } = updatePostResponse;

      if (updated && updated.id) {
        const transformed = transformDistroDashboard(updated);
        setDashboards(prev => prev.map(d => d.id === id ? transformed : d));
      }

      await invalidateAndRefreshDashboard(id);

      return id;
    } catch (error) {
      console.error('Error updating dashboard:', error);
      throw error;
    }
  }

  const saveTemplateStyling = async (dashboardId: string, layout: Array<Layout>, versionId: string) => {
    try {
      const layoutById = new Map(layout.map(l => [l.i, l]));
      const adapted = dashboardsById[dashboardId]?.adapted || [];
      const componentStyles: Array<{ flatIndex: number; _style: { x: number; y: number; w: number; h: number } }> = [];

      // Collect styles recursivley since Dashboard Sections can be nested.
      const collectStyles = (components: AdaptedDashboardComponent[], idPrefix: string, startFlatIdx: number): number => {
        let flatIdx = startFlatIdx;
        for (let i = 0; i < components.length; i++) {
          const comp = components[i];
          const itemId = `${idPrefix}-${i}`;
          const layoutItem = layoutById.get(itemId);
          if (layoutItem) {
            componentStyles.push({
              flatIndex: flatIdx,
              _style: { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h },
            });
          }
          flatIdx++;
          if (Array.isArray(comp.children)) {
            flatIdx = collectStyles(comp.children, itemId, flatIdx);
          }
        }
        return flatIdx;
      };

      collectStyles(adapted, dashboardId, 0);

      const isFromTemplatedBlock = dashboardsById[dashboardId]?._fromTemplatedBlock === true;

      const updatePostResponse = await updateDashboardPost({
        id: dashboardId,
        config: { partialUpdate: true, fields: { componentStyles: { styles: componentStyles, isFromTemplatedBlock } } },
        baseVersionId: versionId,
      });

      if ('error' in updatePostResponse) {
        throw new Error(String(updatePostResponse.error));
      }

      await invalidateAndRefreshDashboard(dashboardId);

      return dashboardId;
    } catch (error) {
      console.error('Error saving template styling:', error);
      throw error;
    }
  }

  const toggleDashboardLocked = useCallback(async (id: string) => {
    try {
      const currentDashboard = dashboardsById[id];
      if (!currentDashboard) return;

      const newLocked = !currentDashboard.locked;
      const baseVersionId = currentDashboard.versionId;

      await updateDashboardPost({
        id,
        config: { partialUpdate: true, fields: { locked: newLocked } },
        baseVersionId,
      });

      // Update local state immediately
      setDashboardsById(prev => {
        const existing = prev[id];
        if (!existing) return prev;
        return { ...prev, [id]: { ...existing, locked: newLocked } };
      });
      setDashboards(prev => prev.map(d => d.id === id ? { ...d, locked: newLocked } : d));

      await invalidateAndRefreshDashboard(id);
    } catch (error) {
      console.error('Error toggling dashboard lock:', error);
      throw error;
    }
  }, [dashboardsById, updateDashboardPost]);

  const logDashboardDownload = async (id: string) => {
    const result = await logAuditEntryPost({
      eventName: "ADMIN_PANEL_DOWNLOADED_DASHBOARD",
      value: { dashboardId: id },
    })

    if (result.error) {
      logAdminPanelError(result.error, result.message)
      return false
    }

    return true
  }

  const value = {
    dashboards,
    reviewDashboards,
    dashboardsById,
    dashboardErrors,
    getDashboards,
    getReviewDashboards,
    isDashboardsLoading,
    hasInitialLoad,

    fetchDashboardById,
    getDashboardById,
    getDashboardAncestors,
    deleteDashboardById,
    cloneDashboard,
    isCloning,

    stagedComponents,
    setStagedComponents,

    createDashboard,
    updateDashboard,
    saveTemplateStyling,
    loadDashboardComponentData,

    toggleDashboardLocked,
    logDashboardDownload
  }
    
  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  )
}
